import { eventBus } from '../core/EventBus';
import { SaveManager, SaveData } from '../core/SaveManager';
import { Game } from '../core/Game';
import { Environment } from '../environment/Environment';
import { Plant } from '../plants/Plant';
import { Lavender, getSpeciesById } from '../plants/PlantData';
import { UIManager } from '../ui/UIManager';
import { PlantManager } from './PlantManager';
import { PlantInfoPanel } from '../ui/PlantInfoPanel';
import { CurrencyManager } from '../economy/CurrencyManager';
import { iconFlower, iconCross, iconDownload, iconUpload } from '../icons';

export class GardenManager {
  private uiLayer: HTMLElement;
  private infoPanels: Map<string, PlantInfoPanel> = new Map();

  constructor(
    private game: Game,
    private environment: Environment,
    private plantManager: PlantManager,
    private uiManager: UIManager,
    private currencyManager: CurrencyManager,
    uiLayer: HTMLElement
  ) {
    this.uiLayer = uiLayer;
    this.setupEventHandlers();
  }

  private setupEventHandlers(): void {
    eventBus.on('canvas:click', (x: number, y: number) => {
      const plant = this.plantManager.findNearestPlant(x, y, 0);
      if (plant) {
        this.showInfoPanel(plant);
      }
    });

    eventBus.on('plant:openInfo', (plant: Plant) => {
      this.showInfoPanel(plant);
    });

    eventBus.on('garden:create', (name: string) => this.createGarden(name));
    eventBus.on('garden:switch', (id: string) => this.switchGarden(id));
    eventBus.on('garden:delete', (id: string) => this.deleteGarden(id));

    eventBus.on('game:save', () => this.saveGame());
    eventBus.on('game:export', () => this.exportSave());
    eventBus.on('game:import', (file: File) => this.importSave(file));

    setInterval(() => this.saveGame(), 60000);
    window.addEventListener('beforeunload', () => this.saveGame());
  }

  initializeGarden(): void {
    const savedData = SaveManager.load();
    if (savedData) {
      this.loadGame(savedData);
      return;
    }

    const gardens = SaveManager.loadAllGardens();
    if (gardens.length > 0) {
      SaveManager.setCurrentId(gardens[0].id);
      this.loadGame(gardens[0].saveData);
      return;
    }

    this.plantManager.clearAllPlants();
    const startPlant = new Plant(Lavender, { x: 0.5, y: 0.78 });
    this.plantManager.addPlant(startPlant);
    const sd = this.game.createSaveData(this.plantManager.toJSON(), this.currencyManager.balance);
    SaveManager.createGarden('我的花园', sd);
  }

  private createGarden(name: string): void {
    this.saveGame();
    this.closeAllInfoPanels();
    this.plantManager.clearAllPlants();
    this.environment = new Environment();
    this.game.gameTime = 0;

    this.currencyManager.balance = CurrencyManager.STARTING_BALANCE;
    eventBus.emit('currency:changed', this.currencyManager.balance);

    const saveData = this.game.createSaveData([], this.currencyManager.balance);
    SaveManager.createGarden(name, saveData);
    this.uiManager.refreshGardenList();
    this.uiManager.showNotification(`${iconFlower({ size: 18 })} 花园「${name}」已创建`);
  }

  private switchGarden(id: string): void {
    this.saveGame();
    const garden = SaveManager.loadGarden(id);
    if (!garden) return;
    SaveManager.setCurrentId(id);
    this.loadGame(garden.saveData);
    this.uiManager.refreshGardenList();
    this.uiManager.showNotification(`${iconFlower({ size: 18 })} 已切换到花园「${garden.name}」`);
  }

  private deleteGarden(id: string): void {
    const garden = SaveManager.loadGarden(id);
    const gardenName = garden?.name || '未知';
    const wasCurrent = SaveManager.getCurrentId() === id;

    SaveManager.deleteGarden(id);

    if (wasCurrent) {
      const gardens = SaveManager.loadAllGardens();
      if (gardens.length > 0) {
        const newCurrent = gardens[0];
        SaveManager.setCurrentId(newCurrent.id);
        this.loadGame(newCurrent.saveData);
      } else {
        this.closeAllInfoPanels();
        this.plantManager.clearAllPlants();
        this.environment = new Environment();
        this.game.gameTime = 0;
        this.currencyManager.balance = CurrencyManager.STARTING_BALANCE;
        eventBus.emit('currency:changed', this.currencyManager.balance);
        const sd = this.game.createSaveData([], this.currencyManager.balance);
        SaveManager.createGarden('我的花园', sd);
      }
    }

    this.uiManager.refreshGardenList();
    this.uiManager.showNotification(`${iconCross({ size: 18 })} 花园「${gardenName}」已删除`);
  }

  saveGame(): void {
    const saveData = this.game.createSaveData(this.plantManager.toJSON(), this.currencyManager.balance);
    const detachedPanels = this.uiManager.getDetachedConsolePanels();
    if (detachedPanels.length > 0) {
      saveData.detachedPanels = detachedPanels;
    }
    SaveManager.save(saveData);
  }

  private loadGame(data: SaveData): void {
    this.game.loadSaveData(data);
    this.currencyManager.balance = data.currency;
    eventBus.emit('currency:changed', this.currencyManager.balance);

    this.closeAllInfoPanels();
    this.plantManager.clearAllPlants();

    if (data.plants && data.plants.length > 0) {
      const plants: Plant[] = [];
      for (const pd of data.plants) {
        const species = getSpeciesById(pd.species) || Lavender;
        const plant = Plant.fromJSON(pd, species);
        plants.push(plant);
      }
      this.plantManager.setPlants(plants);
    }

    if (data.settings) {
      this.game.settings = { ...data.settings };
    }

    this.uiManager.restoreDetachedConsolePanels(data.detachedPanels || []);
  }

  private exportSave(): void {
    const saveData = this.game.createSaveData(this.plantManager.toJSON(), this.currencyManager.balance);
    SaveManager.exportToFile(saveData);
    this.uiManager.showNotification(`${iconDownload({ size: 18 })} 存档已导出`);
  }

  private async importSave(file: File): Promise<void> {
    const data = await SaveManager.importFromFile(file);
    if (data) {
      this.loadGame(data);
      this.uiManager.showNotification(`${iconUpload({ size: 18 })} 存档已导入`);
    } else {
      this.uiManager.showNotification(`${iconCross({ size: 18 })} 无法读取存档文件`);
    }
  }

  private showInfoPanel(plant: Plant): void {
    const existing = this.infoPanels.get(plant.id);
    if (existing) {
      existing.destroy();
    }
    this.uiManager.hidePlantTags();
    const panel = new PlantInfoPanel(plant, this.uiLayer, () => {
      this.infoPanels.delete(plant.id);
    });
    this.infoPanels.set(plant.id, panel);
  }

  private closeAllInfoPanels(): void {
    this.infoPanels.forEach(panel => panel.destroy());
    this.infoPanels.clear();
  }

  getInfoPanels(): Map<string, PlantInfoPanel> {
    return this.infoPanels;
  }

  getEnvironment(): Environment {
    return this.environment;
  }
}
